/**
 * TileMapRenderer - Cell-specific tile map rendering
 *
 * Owns resources specific to a particular cell size:
 * - Texture atlas allocator
 * - Instance data buffer
 * - Offscreen framebuffer
 * - View state (pan/zoom)
 *
 * Uses a shared WebGLContext and TileMapShader.
 */

import { TextureAtlasAllocator, type CellInfo } from './TextureAtlasAllocator';
import type { WebGLContext } from './WebGLContext';
import type { TileMapShader } from './TileMapShader';

export type { CellInfo };

export class TileMapRenderer {
  private ctx: WebGLContext;
  private shader: TileMapShader;
  private gl: WebGL2RenderingContext;

  // Cell dimensions
  readonly cellWidth: number;
  readonly cellHeight: number;

  // Sprite atlas allocator
  private atlasAllocator: TextureAtlasAllocator;

  // View state
  private offsetX = 0;
  private offsetY = 0;
  private scale = 1;

  // Visible tile range (set by caller for culling)
  private visibleStartX = 0;
  private visibleStartY = 0;
  private visibleEndX = 0;
  private visibleEndY = 0;

  // Offscreen framebuffer for pass 1
  private offscreenFB: WebGLFramebuffer | null = null;
  private offscreenTexture: WebGLTexture | null = null;
  private offscreenWidth = 0;
  private offscreenHeight = 0;

  // Instance buffer
  private instanceBuffer: WebGLBuffer | null = null;

  // Pre-allocated instance buffer for rendering (avoid per-frame allocations)
  // 15 floats per instance: tilePos(2) + spriteUV(4) + fgColor(4) + detailColor(4) + atlasLayer(1)
  private instanceData: Float32Array;
  private instanceCount = 0;
  private instanceCapacity: number;

  // AA toggle state
  private aaEnabled = true;

  // Texture array dimensions (from allocator)
  private atlasWidth = 0;
  private atlasHeight = 0;

  // Padding added to offscreen buffer for AA edge sampling
  private static readonly OFFSCREEN_PADDING = 2;

  constructor(
    ctx: WebGLContext,
    shader: TileMapShader,
    cellWidth: number,
    cellHeight: number,
    initialCapacity: number = 8192
  ) {
    this.ctx = ctx;
    this.shader = shader;
    this.gl = ctx.gl;
    this.cellWidth = cellWidth;
    this.cellHeight = cellHeight;

    // Pre-allocate instance buffer
    this.instanceCapacity = initialCapacity;
    this.instanceData = new Float32Array(initialCapacity * 15);

    // Initialize atlas allocator
    this.atlasAllocator = new TextureAtlasAllocator(
      this.gl,
      cellWidth,
      cellHeight,
      512,
      512,
      4,
      256
    );
    this.atlasAllocator.initialize();

    const dims = this.atlasAllocator.getDimensions();
    this.atlasWidth = dims.width;
    this.atlasHeight = dims.height;

    // Create instance buffer
    this.instanceBuffer = this.gl.createBuffer()!;
  }

  /**
   * Get the texture atlas allocator for direct cell allocation.
   */
  getAtlasAllocator(): TextureAtlasAllocator {
    return this.atlasAllocator;
  }

  /**
   * Set view transform (pan and zoom).
   */
  setView(offsetX: number, offsetY: number, scale: number): void {
    this.offsetX = offsetX;
    this.offsetY = offsetY;
    this.scale = Math.max(0.25, Math.min(16, scale));
  }

  /**
   * Get current view transform.
   */
  getView(): { offsetX: number; offsetY: number; scale: number } {
    return { offsetX: this.offsetX, offsetY: this.offsetY, scale: this.scale };
  }

  /**
   * Pan the view by delta pixels.
   */
  pan(deltaX: number, deltaY: number): void {
    this.offsetX += deltaX;
    this.offsetY += deltaY;
  }

  /**
   * Enable or disable pixel-art AA in the compose pass.
   */
  setAAEnabled(enabled: boolean): void {
    this.aaEnabled = enabled;

    // Update offscreen texture filtering mode
    if (this.offscreenTexture) {
      const gl = this.gl;
      gl.bindTexture(gl.TEXTURE_2D, this.offscreenTexture);
      const filter = enabled ? gl.LINEAR : gl.NEAREST;
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    }
  }

  /**
   * Get current AA enabled state.
   */
  isAAEnabled(): boolean {
    return this.aaEnabled;
  }

  /**
   * Set the visible tile range for this frame.
   * Must be called before beginFrame() to properly size the offscreen buffer.
   */
  setVisibleTileRange(startX: number, startY: number, endX: number, endY: number): void {
    this.visibleStartX = startX;
    this.visibleStartY = startY;
    this.visibleEndX = endX;
    this.visibleEndY = endY;
  }

  /**
   * Begin a new frame. Sets up offscreen buffer and clears it.
   */
  beginFrame(): void {
    const gl = this.gl;

    // Calculate required offscreen buffer size based on visible tiles
    const tilesX = this.visibleEndX - this.visibleStartX;
    const tilesY = this.visibleEndY - this.visibleStartY;
    const padding = TileMapRenderer.OFFSCREEN_PADDING;
    const requiredWidth = tilesX * this.cellWidth + padding * 2;
    const requiredHeight = tilesY * this.cellHeight + padding * 2;

    // Resize offscreen buffer if needed
    if (requiredWidth !== this.offscreenWidth || requiredHeight !== this.offscreenHeight) {
      this.resizeOffscreenBuffer(requiredWidth, requiredHeight);
    }

    // Bind offscreen framebuffer and clear with background color
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.offscreenFB);
    gl.viewport(0, 0, this.offscreenWidth, this.offscreenHeight);
    gl.clearColor(0.1, 0.1, 0.1, 1.0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    this.instanceCount = 0;
  }

  /**
   * Add a sprite instance to be rendered.
   */
  addInstance(
    tileX: number,
    tileY: number,
    cellIndex: number,
    fgR: number,
    fgG: number,
    fgB: number,
    fgA: number,
    detailR: number,
    detailG: number,
    detailB: number,
    detailA: number
  ): void {
    // Grow buffer if needed
    if (this.instanceCount >= this.instanceCapacity) {
      this.instanceCapacity *= 2;
      const newData = new Float32Array(this.instanceCapacity * 15);
      newData.set(this.instanceData);
      this.instanceData = newData;
    }

    const cell = this.atlasAllocator.getCellInfo(cellIndex);
    const offset = this.instanceCount * 15;
    const data = this.instanceData;

    data[offset + 0] = tileX;
    data[offset + 1] = tileY;
    data[offset + 2] = cell.x;
    data[offset + 3] = cell.y;
    data[offset + 4] = this.cellWidth;
    data[offset + 5] = this.cellHeight;
    data[offset + 6] = fgR;
    data[offset + 7] = fgG;
    data[offset + 8] = fgB;
    data[offset + 9] = fgA;
    data[offset + 10] = detailR;
    data[offset + 11] = detailG;
    data[offset + 12] = detailB;
    data[offset + 13] = detailA;
    data[offset + 14] = cell.layer;

    this.instanceCount++;
  }

  /**
   * End the frame: execute both render passes.
   */
  endFrame(): void {
    // Pass 1: Draw sprites to offscreen buffer
    if (this.instanceCount > 0) {
      this.drawSpritesPass();
    }

    // Pass 2: Compose to canvas
    this.composePass();
  }

  /**
   * Convert screen coordinates to tile coordinates.
   */
  screenToTile(
    screenX: number,
    screenY: number,
    mapWidth: number,
    mapHeight: number
  ): { x: number; y: number } | null {
    const effectiveTileWidth = this.cellWidth * this.scale;
    const effectiveTileHeight = this.cellHeight * this.scale;

    const tileX = Math.floor((screenX - this.offsetX) / effectiveTileWidth);
    const tileY = Math.floor((screenY - this.offsetY) / effectiveTileHeight);

    if (tileX >= 0 && tileX < mapWidth && tileY >= 0 && tileY < mapHeight) {
      return { x: tileX, y: tileY };
    }

    return null;
  }

  /**
   * Zoom by a multiplicative factor centered on a specific point.
   */
  zoomAt(mouseX: number, mouseY: number, zoomDelta: number): void {
    const oldScale = this.scale;
    const newScale = Math.max(0.25, Math.min(8, oldScale * zoomDelta));

    const tileX = (mouseX - this.offsetX) / (this.cellWidth * oldScale);
    const tileY = (mouseY - this.offsetY) / (this.cellHeight * oldScale);

    this.offsetX = mouseX - tileX * (this.cellWidth * newScale);
    this.offsetY = mouseY - tileY * (this.cellHeight * newScale);
    this.scale = newScale;
  }

  /**
   * Zoom by a factor centered on the canvas center.
   */
  zoomBy(factor: number): void {
    const { width, height } = this.ctx.getPhysicalDimensions();
    const centerX = width / 2;
    const centerY = height / 2;
    const zoomDelta = 1 + factor;
    this.zoomAt(centerX, centerY, zoomDelta);
  }

  /**
   * Debug: dump atlas texture layer to a new window.
   */
  debugDumpAtlas(layer: number = 0): void {
    const gl = this.gl;
    const texture = this.atlasAllocator.getTexture();
    if (!texture) {
      console.error('No atlas texture');
      return;
    }

    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, texture, 0, layer);

    const pixels = new Uint8Array(this.atlasWidth * this.atlasHeight * 4);
    gl.readPixels(0, 0, this.atlasWidth, this.atlasHeight, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fb);

    // Flip Y
    const flipped = new Uint8Array(pixels.length);
    const rowSize = this.atlasWidth * 4;
    for (let y = 0; y < this.atlasHeight; y++) {
      const srcRow = (this.atlasHeight - 1 - y) * rowSize;
      const dstRow = y * rowSize;
      flipped.set(pixels.subarray(srcRow, srcRow + rowSize), dstRow);
    }

    const canvas = document.createElement('canvas');
    canvas.width = this.atlasWidth;
    canvas.height = this.atlasHeight;
    const ctx2d = canvas.getContext('2d')!;
    const imageData = new ImageData(
      new Uint8ClampedArray(flipped),
      this.atlasWidth,
      this.atlasHeight
    );
    ctx2d.putImageData(imageData, 0, 0);

    const win = window.open('', '_blank', `width=${this.atlasWidth},height=${this.atlasHeight}`);
    if (win) {
      win.document.body.style.margin = '0';
      win.document.body.style.background = '#333';
      win.document.body.appendChild(canvas);
      win.document.title = `Atlas Layer ${layer}`;
    }
  }

  /**
   * Dispose of renderer resources.
   */
  dispose(): void {
    const gl = this.gl;
    if (this.offscreenFB) gl.deleteFramebuffer(this.offscreenFB);
    if (this.offscreenTexture) gl.deleteTexture(this.offscreenTexture);
    if (this.instanceBuffer) gl.deleteBuffer(this.instanceBuffer);
    this.atlasAllocator.dispose();
  }

  // ============ Private methods ============

  private resizeOffscreenBuffer(width: number, height: number): void {
    const gl = this.gl;

    width = Math.max(1, Math.min(4096, width));
    height = Math.max(1, Math.min(4096, height));

    if (!this.offscreenFB) {
      this.offscreenFB = gl.createFramebuffer();
    }

    if (this.offscreenTexture) {
      gl.deleteTexture(this.offscreenTexture);
    }

    this.offscreenTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.offscreenTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

    const filter = this.aaEnabled ? gl.LINEAR : gl.NEAREST;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.offscreenFB);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      this.offscreenTexture,
      0
    );

    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
      console.error('Framebuffer incomplete:', status);
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    this.offscreenWidth = width;
    this.offscreenHeight = height;
  }

  private drawSpritesPass(): void {
    const gl = this.gl;
    const shader = this.shader;

    this.shader.useSpriteProgram();

    const padding = TileMapRenderer.OFFSCREEN_PADDING;
    gl.uniform2f(shader.spriteUniforms.resolution, this.offscreenWidth, this.offscreenHeight);
    gl.uniform2f(shader.spriteUniforms.tileSize, this.cellWidth, this.cellHeight);
    gl.uniform2f(shader.spriteUniforms.tileOffset, this.visibleStartX, this.visibleStartY);
    gl.uniform2f(shader.spriteUniforms.atlasArraySize, this.atlasWidth, this.atlasHeight);
    gl.uniform2f(shader.spriteUniforms.pixelOffset, padding, padding);

    // Bind texture array
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.atlasAllocator.getTexture());

    // Setup quad vertex attribute
    gl.bindBuffer(gl.ARRAY_BUFFER, shader.quadBuffer);
    gl.enableVertexAttribArray(shader.spriteAttribs.position);
    gl.vertexAttribPointer(shader.spriteAttribs.position, 2, gl.FLOAT, false, 0, 0);

    // Upload instance data
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      this.instanceData.subarray(0, this.instanceCount * 15),
      gl.DYNAMIC_DRAW
    );

    // Setup instance attributes
    const stride = 15 * 4;

    gl.enableVertexAttribArray(shader.spriteAttribs.tilePos);
    gl.vertexAttribPointer(shader.spriteAttribs.tilePos, 2, gl.FLOAT, false, stride, 0);
    gl.vertexAttribDivisor(shader.spriteAttribs.tilePos, 1);

    gl.enableVertexAttribArray(shader.spriteAttribs.spriteUV);
    gl.vertexAttribPointer(shader.spriteAttribs.spriteUV, 4, gl.FLOAT, false, stride, 2 * 4);
    gl.vertexAttribDivisor(shader.spriteAttribs.spriteUV, 1);

    gl.enableVertexAttribArray(shader.spriteAttribs.fgColor);
    gl.vertexAttribPointer(shader.spriteAttribs.fgColor, 4, gl.FLOAT, false, stride, 6 * 4);
    gl.vertexAttribDivisor(shader.spriteAttribs.fgColor, 1);

    gl.enableVertexAttribArray(shader.spriteAttribs.detailColor);
    gl.vertexAttribPointer(shader.spriteAttribs.detailColor, 4, gl.FLOAT, false, stride, 10 * 4);
    gl.vertexAttribDivisor(shader.spriteAttribs.detailColor, 1);

    gl.enableVertexAttribArray(shader.spriteAttribs.atlasLayer);
    gl.vertexAttribPointer(shader.spriteAttribs.atlasLayer, 1, gl.FLOAT, false, stride, 14 * 4);
    gl.vertexAttribDivisor(shader.spriteAttribs.atlasLayer, 1);

    // Draw all instances
    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, this.instanceCount);
  }

  private composePass(): void {
    const gl = this.gl;
    const shader = this.shader;
    const canvas = this.ctx.canvas;

    // Switch from offscreen FB back to canvas
    this.ctx.bindDefaultFramebuffer();

    shader.useComposeProgram();

    const dpr = this.ctx.getDevicePixelRatio();
    const padding = TileMapRenderer.OFFSCREEN_PADDING;

    gl.uniform2f(shader.composeUniforms.canvasSize, canvas.width, canvas.height);
    gl.uniform2f(shader.composeUniforms.offscreenSize, this.offscreenWidth, this.offscreenHeight);
    gl.uniform2f(shader.composeUniforms.viewOffset, this.offsetX * dpr, this.offsetY * dpr);
    gl.uniform1f(shader.composeUniforms.scale, this.scale);
    gl.uniform2f(shader.composeUniforms.tileOffset, this.visibleStartX, this.visibleStartY);
    gl.uniform2f(shader.composeUniforms.tileSize, this.cellWidth * dpr, this.cellHeight * dpr);
    gl.uniform2f(shader.composeUniforms.padding, padding, padding);
    gl.uniform1f(shader.composeUniforms.aaEnabled, this.aaEnabled ? 1.0 : 0.0);
    gl.uniform2f(shader.composeUniforms.baseTileSize, this.cellWidth, this.cellHeight);

    // Bind offscreen texture
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.offscreenTexture);
    gl.uniform1i(shader.composeUniforms.offscreenTex, 0);

    // Setup quad vertex attribute
    gl.bindBuffer(gl.ARRAY_BUFFER, shader.quadBuffer);
    gl.enableVertexAttribArray(shader.composeAttribs.position);
    gl.vertexAttribPointer(shader.composeAttribs.position, 2, gl.FLOAT, false, 0, 0);

    // Draw full-screen quad
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }
}
